import { auth, db, storage } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * Upload a single file to Firebase Storage and return its download URL.
 * @param {File} file - The file object to upload.
 * @param {string} userId - The UID of the current user.
 * @returns {Promise<{ name: string, url: string }>} The file's name and download URL.
 */
const uploadSingleFile = async (file, userId) => {
  const storagePath = `uploads/${userId}/ApplicationDetails/AdditionalFiles/${file.name}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file);
  const downloadURL = await getDownloadURL(fileRef);
  return { name: file.name, url: downloadURL };
};

/**
 * Uploads the given files and stores their URLs in Firestore under a 'files' field.
 * @param {File[]} newFiles - An array of File objects dropped or selected by the user.
 * @returns {Promise<void>}
 */
export const uploadAdditionalFiles = async (newFiles) => {
  const user = auth.currentUser;
  if (!user) {
    // console.error("No authenticated user. Cannot upload files.");
    return;
  }

  const docRef = doc(db, "Advisers", user.uid, "Personal Details", "Data");
  const docSnap = await getDoc(docRef);

  // If the doc doesn't exist, we'll create it. If it does, we use existing data.
  let currentData = {};
  if (docSnap.exists()) {
    currentData = docSnap.data();
  }

  // Upload all new files and get their URLs
  const uploadedFiles = [];
  for (const file of newFiles) {
    const uploadedFileInfo = await uploadSingleFile(file, user.uid);
    uploadedFiles.push(uploadedFileInfo);
  }

  // Merge new files with any existing files array
  const existingFiles = currentData.files || [];
  const updatedFiles = [...existingFiles, ...uploadedFiles];

  // Save updated files array back to Firestore
  await setDoc(docRef, { files: updatedFiles }, { merge: true });

  // console.log("Files successfully uploaded and URLs stored in Firestore.");
};
